import { useToast } from '@chakra-ui/react';
import { AxiosResponse } from 'axios';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { EquipmentModelView, Pagination, SelectItem } from '~/types';
import { queryClient } from '~/utils/react-query';
import {
  createEquipmentModel,
  getEquipmentModels,
  getEquipmentModelsAsSelect,
  updateEquipmentModelById,
} from '../../services';
import { CreatePayload, UpdatePayload } from './types';

export function useEquipmentModels(
  page: number,
  search = '',
  options: UseQueryOptions<Pagination<EquipmentModelView>, unknown> = {},
): UseQueryResult<Pagination<EquipmentModelView>, unknown> {
  return useQuery<Pagination<EquipmentModelView>, unknown>(
    ['equipmentModels', page, search],
    () => getEquipmentModels(page, search),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
    },
  );
}

export function useEquipmentModelsAsSelect(
  options: UseQueryOptions<SelectItem[], unknown> = {},
): UseQueryResult<SelectItem[], unknown> {
  return useQuery<SelectItem[], unknown>(
    ['equipmentModelsSelect'],
    () => getEquipmentModelsAsSelect(),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
    },
  );
}

export function useUpdateEquipmentModel(): UseMutationResult<
  AxiosResponse<any>,
  unknown,
  UpdatePayload,
  unknown
> {
  const toast = useToast();

  return useMutation(
    async ({ id, formData }: UpdatePayload) =>
      updateEquipmentModelById(id, formData),

    {
      onSuccess: () => {
        toast({
          title: 'Alterado com sucesso',
          description: 'Dados alterados com sucesso!',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        queryClient.invalidateQueries('equipmentModels');
        queryClient.invalidateQueries('equipmentModelsSelect');
      },
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro ao atualizar dados, tente novamente',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );
}

export function useCreateEquipmentModel(): UseMutationResult<
  AxiosResponse<any>,
  unknown,
  CreatePayload,
  unknown
> {
  const toast = useToast();

  return useMutation(
    async ({ formData }: CreatePayload) => createEquipmentModel(formData),
    {
      onSuccess: () => {
        toast({
          title: 'Criado com sucesso',
          description: 'modelo criado com sucesso!',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        queryClient.invalidateQueries('equipmentModels');
        queryClient.invalidateQueries('equipmentModelsSelect');
      },
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro ao criar modelo, tente novamente',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );
}
